import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Download,
  TrendingUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Zap,
  Package,
} from 'lucide-react';
import type { RouterOutputs } from '@/lib/trpc';

type StockAlertItem =
  RouterOutputs['stockAlerts']['getStockIntelligence']['fastMovers'][number];
type FastMoversSummary =
  RouterOutputs['stockAlerts']['getStockIntelligence']['fastMoversSummary'];

interface Props {
  items: StockAlertItem[] | undefined;
  summary: FastMoversSummary | undefined;
  isLoading: boolean;
  hasLocation: boolean;
}

type SortField =
  | 'dailySales'
  | 'sku'
  | 'totalSold30d'
  | 'available'
  | 'daysRemaining'
  | 'effectiveCoverDays';

const PAGE_SIZE = 50;

function PriorityBadge({
  priority,
}: {
  priority: StockAlertItem['priority'];
}) {
  const config: Record<
    string,
    { variant: 'destructive' | 'secondary' | 'default' | 'outline'; label: string }
  > = {
    critical: { variant: 'destructive', label: 'Critical' },
    high: { variant: 'default', label: 'High' },
    medium: { variant: 'secondary', label: 'Medium' },
    low: { variant: 'outline', label: 'Low' },
  };
  const c = config[priority] ?? config.low;
  return <Badge variant={c!.variant}>{c!.label}</Badge>;
}

function exportToCsv(items: StockAlertItem[]) {
  const headers = [
    'SKU',
    'Style',
    'Size',
    'Color',
    'Daily Sales',
    '30d Sold',
    'Available',
    'Days Left',
    'Incoming',
    'Eff. Cover (days)',
    'Priority',
  ];
  const rows = items.map((i) => [
    i.sku,
    i.category ?? '',
    i.size ?? '',
    i.color ?? '',
    i.dailySales != null ? i.dailySales.toFixed(1) : '',
    i.totalSold30d,
    i.available,
    i.daysRemaining !== null ? i.daysRemaining : '',
    i.incoming,
    i.effectiveCoverDays !== null ? i.effectiveCoverDays : '',
    i.priority,
  ]);

  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fast-movers-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function FastMoversSection({
  items,
  summary,
  isLoading,
  hasLocation,
}: Props) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('dailySales');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);

  const categories = useMemo(() => {
    if (!items) return [];
    const set = new Set(
      items.map((i) => i.category).filter(Boolean) as string[]
    );
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    let result = items;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (i) =>
          i.sku.toLowerCase().includes(q) ||
          i.category?.toLowerCase().includes(q) ||
          i.title?.toLowerCase().includes(q)
      );
    }

    if (categoryFilter !== 'all') {
      result = result.filter((i) => i.category === categoryFilter);
    }

    result = [...result].sort((a, b) => {
      let aVal: number;
      let bVal: number;
      switch (sortField) {
        case 'sku':
          return sortAsc
            ? a.sku.localeCompare(b.sku)
            : b.sku.localeCompare(a.sku);
        case 'totalSold30d':
          aVal = a.totalSold30d;
          bVal = b.totalSold30d;
          break;
        case 'available':
          aVal = a.available;
          bVal = b.available;
          break;
        case 'daysRemaining':
          aVal = a.daysRemaining ?? 9999;
          bVal = b.daysRemaining ?? 9999;
          break;
        case 'effectiveCoverDays':
          aVal = a.effectiveCoverDays ?? 9999;
          bVal = b.effectiveCoverDays ?? 9999;
          break;
        default:
          aVal = a.dailySales ?? 0;
          bVal = b.dailySales ?? 0;
      }
      return sortAsc ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [items, search, categoryFilter, sortField, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  if (safePage !== page) setPage(safePage);

  const pageItems = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
    setPage(1);
  };

  const SortHeader = ({
    field,
    children,
    className,
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={`cursor-pointer select-none ${className ?? ''}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
      </div>
    </TableHead>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-3">
        <TrendingUp className="h-5 w-5 text-violet-500" />
        <div className="flex-1">
          <CardTitle className="text-base">Fast Movers</CardTitle>
          <p className="text-sm text-muted-foreground">
            Top selling SKUs by velocity over the last 30 days
          </p>
        </div>
        {filtered.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToCsv(filtered)}
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary metrics */}
        {summary && !isLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
              <Zap className="h-4 w-4 text-violet-500 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Avg Velocity</p>
                <p className="text-sm font-semibold">
                  {summary.avgDailyVelocity}/day
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
              <Package className="h-4 w-4 text-blue-500 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">30d Units Sold</p>
                <p className="text-sm font-semibold">
                  {summary.totalUnitsSold30d.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
              <TrendingUp className="h-4 w-4 text-emerald-500 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">
                  SKUs with Sales
                </p>
                <p className="text-sm font-semibold">
                  {summary.totalFastMovers}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">At Risk</p>
                <p className="text-sm font-semibold">
                  {summary.atRiskCount} SKUs
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Search SKU, style, or title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="sm:max-w-xs"
          />
          <Select
            value={categoryFilter}
            onValueChange={(v) => {
              setCategoryFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="sm:w-[200px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-sm text-muted-foreground self-center">
            {filtered.length} SKUs
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[48px]"></TableHead>
                <SortHeader field="sku">SKU</SortHeader>
                <TableHead>Style</TableHead>
                <SortHeader field="dailySales">Daily Sales</SortHeader>
                <SortHeader field="totalSold30d">30d Sold</SortHeader>
                <SortHeader field="available">Available</SortHeader>
                <SortHeader field="daysRemaining">Days Left</SortHeader>
                <TableHead>Incoming</TableHead>
                <SortHeader field="effectiveCoverDays">Eff. Cover</SortHeader>
                <TableHead>Priority</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10} className="h-20">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {search || categoryFilter !== 'all'
                      ? 'No matching SKUs found'
                      : 'No fast movers in the last 30 days'}
                  </TableCell>
                </TableRow>
              )}
              {pageItems.map((item) => (
                <TableRow key={item.skuId}>
                  <TableCell>
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.sku}
                        className="h-8 w-8 rounded object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded bg-muted" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{item.sku}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.category || '-'}
                  </TableCell>
                  <TableCell className="font-medium">
                    {item.dailySales != null ? `${item.dailySales.toFixed(1)}/day` : '-'}
                  </TableCell>
                  <TableCell>{item.totalSold30d}</TableCell>
                  <TableCell>
                    <span
                      className={
                        item.available <= 0
                          ? 'text-red-600 font-medium'
                          : item.available <= (item.notifyThreshold ?? 0)
                            ? 'text-amber-600 font-medium'
                            : ''
                      }
                    >
                      {item.available}
                    </span>
                  </TableCell>
                  <TableCell>
                    {item.daysRemaining !== null ? (
                      <span
                        className={
                          item.daysRemaining <= 3
                            ? 'text-red-600 font-medium'
                            : item.daysRemaining <= 7
                              ? 'text-amber-600'
                              : ''
                        }
                      >
                        {item.daysRemaining}d{!hasLocation ? '*' : ''}
                      </span>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {item.incoming > 0 ? (
                      <span className="text-emerald-600">{item.incoming}</span>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {item.effectiveCoverDays !== null
                      ? `${item.effectiveCoverDays}d`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <PriorityBadge priority={item.priority} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-muted-foreground">
              Showing {(safePage - 1) * PAGE_SIZE + 1}-
              {Math.min(safePage * PAGE_SIZE, filtered.length)} of{' '}
              {filtered.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => setPage(safePage - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {safePage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => setPage(safePage + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
